import { ScrollTrigger } from "gsap/ScrollTrigger";
import { gsap } from "gsap";


/**
 * ATENA CONSULTING — blocco-numeri.js
 *
 * Sequenza animazione:
 *   1. Linea top   → scaleX 0→1
 *   2. Label testo → fade in (prima dei numeri)
 *   3. Celle       → fade in
 *   4. Linea mid + bottom → scaleX 0→1
 *   5. Counter     → digit-by-digit O linear (via data-counter-type)
 *
 * ACF campo: tipo_counter → select: "digit" | "linear"
 * Twig:      data-counter-type="{{ item.tipo_counter|default('digit') }}"
 */

const bloccoNumeri = () => {

  const CFG = {
    start:          'top 80%',
    ease:           'power3.out',
    easeCounter:    'power4.out',
    t: {
      line:    0.7,
      cell:    0.5,
      label:   0.55,
      counter: 2.0,
    },
    labelStagger:   0.07,
    counterStagger: 0.15,
    cellDelay:      0.08,
    digitDelay:     0.18,
  };

  // ── COUNTER LINEAR ───────────────────────────────────────
  // Incrementa di 1 in 1 fino al valore finale
  // con micro fade alpha ad ogni cambio cifra
  function animateLinear(el, to, duration, delay) {
    // Ripristina testo semplice (no span per cifra)
    el.innerHTML = '0';

    const obj = { val: 0 };
    gsap.to(obj, {
      val:      to,
      duration,
      delay,
      ease:     CFG.easeCounter,
      onUpdate: () => {
        const current = Math.round(obj.val);
        const prev    = parseInt(el.textContent.replace(/\D/g, ''), 10);
        if (current !== prev) {
          gsap.fromTo(el,
            { opacity: 0.35 },
            { opacity: 1, duration: 0.12, ease: 'power2.out', overwrite: true }
          );
          el.textContent = current;
        }
      },
      onComplete: () => { el.textContent = Math.round(to); },
    });
  }

  // ── COUNTER DIGIT-BY-DIGIT ───────────────────────────────
  // Ogni cifra parte da 0 e sale al suo valore finale
  // con stagger sx→dx e micro fade alpha ad ogni cambio
  function animateDigit(el, to, duration, delay) {
    const targetStr = String(Math.round(to));
    const numDigits = targetStr.length;

    el.innerHTML = targetStr
      .split('')
      .map(() => `<span class="counter-digit">0</span>`)
      .join('');

    el.querySelectorAll('.counter-digit').forEach((digitEl, i) => {
      const targetDigit = parseInt(targetStr[i], 10);

      // Cifra finale 0 — statica
      if (targetDigit === 0) {
        digitEl.textContent = '0';
        return;
      }

      const obj = { val: 0 };
      gsap.to(obj, {
        val:      targetDigit,
        duration: duration * (0.6 + (numDigits - i) * 0.12),
        delay:    delay + (i * CFG.digitDelay),
        ease:     CFG.easeCounter,
        onUpdate: () => {
          const current = Math.round(obj.val);
          const prev    = parseInt(digitEl.textContent, 10);
          if (current !== prev) {
            gsap.fromTo(digitEl,
              { opacity: 0.35 },
              { opacity: 1, duration: 0.12, ease: 'power2.out', overwrite: true }
            );
            digitEl.textContent = current;
          }
        },
        onComplete: () => { digitEl.textContent = targetDigit; },
      });
    });
  }

  // ── DISPATCHER ──────────────────────────────────────────
  function animateCounter(el, to, duration, delay) {
    const type = el.dataset.counterType || 'digit';
    if (type === 'linear') {
      animateLinear(el, to, duration, delay);
    } else {
      animateDigit(el, to, duration, delay);
    }
  }

  // ── SET INIZIALE ────────────────────────────────────────
  function setInitialStates(block) {
    block.querySelectorAll('.blocco_numeri__line--top, .blocco_numeri__line--mid, .blocco_numeri__line--bottom')
      .forEach(line => gsap.set(line, { scaleX: 0, transformOrigin: 'left center' }));

    block.querySelectorAll('.blocco_numeri__cell')
      .forEach(cell => gsap.set(cell, { opacity: 0 }));

    block.querySelectorAll('.blocco_numeri__label')
      .forEach(label => gsap.set(label, { opacity: 0 }));

    // Testo iniziale con tutti 0 — stessa lunghezza del target
    block.querySelectorAll('.counter-value').forEach(el => {
      const to = String(Math.round(parseFloat(el.dataset.to || 0)));
      el.textContent = '0'.repeat(to.length);
    });
  }

  // ── INIT ────────────────────────────────────────────────
  function init() {

    document.querySelectorAll('[data-block-animate]').forEach(block => {

      setInitialStates(block);

      const grandiCells = Array.from(block.querySelectorAll('.blocco_numeri__row--grandi .blocco_numeri__cell'));
      const piccoliCells = Array.from(block.querySelectorAll('.blocco_numeri__row--piccoli .blocco_numeri__cell'));
      const allCells    = [...grandiCells, ...piccoliCells];
      const allLabels   = Array.from(block.querySelectorAll('.blocco_numeri__label'));
      const allCounters = Array.from(block.querySelectorAll('.counter-value'));

      const lineTop    = block.querySelector('.blocco_numeri__line--top');
      const lineMid    = block.querySelector('.blocco_numeri__line--mid');
      const lineBottom = block.querySelector('.blocco_numeri__line--bottom');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: block,
          start:   CFG.start,
          once:    true,
        },
        defaults: { ease: CFG.ease },
      });

      // 1. Linea top
      if (lineTop) tl.to(lineTop, { scaleX: 1, duration: CFG.t.line });

      // 2. Label → fade PRIMA dei numeri
      if (allLabels.length) {
        tl.to(allLabels, {
          opacity:  1,
          duration: CFG.t.label,
          stagger:  CFG.labelStagger,
        }, '<0.2');
      }

      // 3. Celle → fade (rivela i numeri a "0")
      if (allCells.length) {
        tl.to(allCells, {
          opacity:  1,
          duration: CFG.t.cell,
          stagger:  CFG.cellDelay,
        }, '<0.15');
      }

      // 4. Linea mid
      if (lineMid) tl.to(lineMid, { scaleX: 1, duration: CFG.t.line * 0.8 }, '<0.1');

      // 5. Linea bottom
      if (lineBottom) tl.to(lineBottom, { scaleX: 1, duration: CFG.t.line * 0.8 }, '<0.2');

      // 6. Counter — digit o linear in base a data-counter-type
      allCounters.forEach((counterEl, i) => {
        const to = parseFloat(counterEl.dataset.to || 0);
        tl.add(() => {
          animateCounter(counterEl, to, CFG.t.counter, 0);
        }, `<${i * CFG.counterStagger}`);
      });

    });
  }

  // ── ESECUZIONE ──────────────────────────────────────────
  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }

};

export default bloccoNumeri;